/**
 * UnifiedOnboarding — Canonical 9-screen onboarding (Phase 3)
 *
 * Canvas Sage design system with panoramic zone backgrounds,
 * glassmorphism cards, stepper inputs, and staggered reveal animations.
 *
 * Screens: Welcome (0) → Convergence (1) → Forensic Roast (2) →
 * Bridge (3) → Monthly Burn (4) → Studio Hours (5) →
 * Career Stage (6) → Location/ZIP (7) → Final (8)
 *
 * Sets onboardingCompleted: true on finish.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  StudioProfile,
  STUDIO_DEFAULTS,
  readStudioProfile,
  toProfileUpdate,
} from '@/components/Studio/types';
import type { CareerStageKey } from '@/types';
import { usePlaidConnection } from '@/hooks/usePlaidLink';

/* ═══════════════════════════════════════════════════════
   Design Tokens — Canvas Sage
   ═══════════════════════════════════════════════════════ */

const T = {
  bg: '#FAF8F3',
  surface: 'rgba(255,255,255,0.72)',
  surfSolid: '#FFFFFF',
  text: '#1A1A1A',
  textSub: '#6B6B6B',
  textMuted: '#A8A5A0',
  teal: '#3DABA5',
  tealLight: 'rgba(61,171,165,0.06)',
  border: '#E8E5DE',
  borderLight: '#F0EDE6',
  radius: 20,
  blur: 'blur(20px)',
  shadow: '0 8px 32px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
} as const;

const FONT = "'DM Sans', system-ui, -apple-system, sans-serif";
const FONT_LINK = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap';
const MONO = "'JetBrains Mono','DM Sans',monospace";

/* ═══════════════════════════════════════════════════════
   Panorama Config
   ═══════════════════════════════════════════════════════ */

const ZONE_W = 375;
/* Zone images mapped to screens by content:
   0=Welcome(easel), 1=Convergence(calc), 2=Roast(palette→checklist),
   3=Bridge(TP card), 4=Burn(bills), 5=Hours(clock), 6=Career(gallery),
   7=Location(pin), 8=Final(gears+check) */
const ZONES = [
  { label: 'Creative World', img: '/brand/Zone-0.png' },
  { label: 'Pricing Meets Art', img: '/brand/ZONE-1.png' },
  { label: 'Your Forensic Edge', img: '/brand/ZONE-2.png' },
  { label: 'Art Becomes Data', img: '/brand/ZONE-7.png' },
  { label: 'Cost of Existing', img: '/brand/ZONE-3.png' },
  { label: 'Time / Capacity', img: '/brand/ZONE-4.png' },
  { label: 'Career / Gallery', img: '/brand/ZONE-5.png' },
  { label: 'Location', img: '/brand/ZONE-8.png' },
  { label: 'Your Floor', img: '/brand/ZONE-6.png' },
];

const CAREER_ITEMS: { id: CareerStageKey; label: string; desc: string }[] = [
  { id: 'unlisted', label: 'Unlisted', desc: 'Not actively selling yet' },
  { id: 'emerging', label: 'Emerging', desc: 'Early sales, inconsistent demand' },
  { id: 'working', label: 'Working', desc: 'Regular sales, pricing pressure exists' },
  { id: 'established', label: 'Established', desc: 'Proven market, pricing leverage' },
];

/* ZIP → city/state lookup via Zippopotam.us API with local cache fallback */
const ZIP_CACHE: Record<string, { city: string; state: string }> = {
  '10001': { city: 'New York', state: 'NY' }, '10011': { city: 'New York', state: 'NY' },
  '10013': { city: 'New York', state: 'NY' }, '11201': { city: 'Brooklyn', state: 'NY' },
  '90001': { city: 'Los Angeles', state: 'CA' }, '90012': { city: 'Los Angeles', state: 'CA' },
  '90210': { city: 'Beverly Hills', state: 'CA' }, '90401': { city: 'Santa Monica', state: 'CA' },
  '91604': { city: 'Studio City', state: 'CA' }, '91601': { city: 'North Hollywood', state: 'CA' },
  '91602': { city: 'North Hollywood', state: 'CA' }, '91607': { city: 'Valley Village', state: 'CA' },
  '91423': { city: 'Sherman Oaks', state: 'CA' }, '91403': { city: 'Sherman Oaks', state: 'CA' },
  '91505': { city: 'Burbank', state: 'CA' }, '90028': { city: 'Hollywood', state: 'CA' },
  '90046': { city: 'West Hollywood', state: 'CA' }, '90036': { city: 'Los Angeles', state: 'CA' },
  '94102': { city: 'San Francisco', state: 'CA' }, '94110': { city: 'San Francisco', state: 'CA' },
  '60601': { city: 'Chicago', state: 'IL' }, '60614': { city: 'Chicago', state: 'IL' },
  '77002': { city: 'Houston', state: 'TX' }, '78701': { city: 'Austin', state: 'TX' },
  '75201': { city: 'Dallas', state: 'TX' }, '33101': { city: 'Miami', state: 'FL' },
  '33139': { city: 'Miami Beach', state: 'FL' }, '98101': { city: 'Seattle', state: 'WA' },
  '97201': { city: 'Portland', state: 'OR' }, '80202': { city: 'Denver', state: 'CO' },
  '85001': { city: 'Phoenix', state: 'AZ' }, '30306': { city: 'Atlanta', state: 'GA' },
  '37203': { city: 'Nashville', state: 'TN' }, '02116': { city: 'Boston', state: 'MA' },
  '19103': { city: 'Philadelphia', state: 'PA' }, '87501': { city: 'Santa Fe', state: 'NM' },
  '28801': { city: 'Asheville', state: 'NC' }, '48201': { city: 'Detroit', state: 'MI' },
  '55401': { city: 'Minneapolis', state: 'MN' }, '70116': { city: 'New Orleans', state: 'LA' },
  '96813': { city: 'Honolulu', state: 'HI' }, '20001': { city: 'Washington', state: 'DC' },
};

async function lookupZip(zip: string): Promise<{ city: string; state: string } | null> {
  // Check local cache first
  if (ZIP_CACHE[zip]) return ZIP_CACHE[zip];
  // Try API
  try {
    const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
    if (!res.ok) return null;
    const data = await res.json();
    const place = data.places?.[0];
    if (!place) return null;
    const result = { city: place['place name'], state: place['state abbreviation'] };
    ZIP_CACHE[zip] = result; // Cache for subsequent calls
    return result;
  } catch {
    return null;
  }
}

/* ═══════════════════════════════════════════════════════
   Shared UI Components
   ═══════════════════════════════════════════════════════ */

function Panorama() {
  return (
    <div style={{ display: 'flex', width: ZONE_W * ZONES.length, height: 200 }}>
      {ZONES.map((z, i) => (
        <div key={i} style={{
          width: ZONE_W, height: 200, flexShrink: 0, overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: T.bg,
        }}>
          <img
            src={z.img}
            alt={z.label}
            style={{
              height: '100%', width: 'auto', maxWidth: '100%',
              objectFit: 'contain',
            }}
          />
        </div>
      ))}
    </div>
  );
}

function GlassCard({ children, style = {}, padding = '24px 22px' }: {
  children: React.ReactNode; style?: React.CSSProperties; padding?: string;
}) {
  return (
    <div style={{
      background: T.surface, backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
      borderRadius: T.radius, border: `1px solid ${T.border}`,
      boxShadow: T.shadow, padding, ...style,
    }}>
      {children}
    </div>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'inline-flex', padding: '5px 12px', background: T.tealLight, borderRadius: 100, marginBottom: 14 }}>
      <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.teal, fontFamily: FONT }}>{children}</span>
    </div>
  );
}

function H1({ children, style = {} }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <h1 style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', color: T.text, margin: 0, fontFamily: FONT, ...style }}>{children}</h1>;
}

function Body({ children, style = {} }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <p style={{ fontSize: 14.5, lineHeight: 1.55, color: T.textSub, margin: 0, fontFamily: FONT, ...style }}>{children}</p>;
}

function Btn({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      width: '100%', padding: '17px 24px',
      background: disabled ? T.textMuted : T.teal,
      color: '#fff', border: 'none', borderRadius: 14,
      fontSize: 15.5, fontWeight: 600, fontFamily: FONT,
      cursor: disabled ? 'not-allowed' : 'pointer',
      boxShadow: disabled ? 'none' : '0 4px 16px rgba(61,171,165,0.3)',
      opacity: disabled ? 0.5 : 1,
      transition: 'all 0.2s ease',
    }}>
      {children}
    </button>
  );
}

function Dots({ current, total = 9 }: { current: number; total?: number }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', padding: '8px 0' }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          width: i === current ? 24 : 6, height: 6, borderRadius: 3,
          background: i <= current ? T.teal : 'rgba(61,171,165,0.15)',
          opacity: i === current ? 1 : i < current ? 0.4 : 1,
          transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1)',
        }} />
      ))}
    </div>
  );
}

function Stepper({ value, onChange, prefix = '$', suffix = '', step = 100, min = 0, max = 99999 }: {
  value: number; onChange: (v: number) => void; prefix?: string; suffix?: string;
  step?: number; min?: number; max?: number;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', background: T.surfSolid, borderRadius: 16, border: `1.5px solid ${T.border}`, overflow: 'hidden' }}>
      <button onClick={() => onChange(Math.max(min, value - step))} style={{
        width: 56, height: 64, border: 'none', background: 'transparent',
        fontSize: 22, color: T.textSub, cursor: 'pointer',
        borderRight: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: FONT,
      }}>{'\u2212'}</button>
      <div style={{ flex: 1, textAlign: 'center', fontSize: 30, fontWeight: 700, color: T.text, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums', fontFamily: FONT }}>
        {prefix}{value.toLocaleString()}{suffix}
      </div>
      <button onClick={() => onChange(Math.min(max, value + step))} style={{
        width: 56, height: 64, border: 'none', background: 'transparent',
        fontSize: 22, color: T.textSub, cursor: 'pointer',
        borderLeft: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: FONT,
      }}>+</button>
    </div>
  );
}

function Radio({ label, desc, selected, onClick }: {
  label: string; desc: string; selected: boolean; onClick: () => void;
}) {
  return (
    <button onClick={onClick} style={{
      width: '100%', padding: '14px 16px',
      background: selected ? 'rgba(61,171,165,0.06)' : T.surfSolid,
      border: `2px solid ${selected ? T.teal : 'rgba(0,0,0,0.06)'}`,
      borderRadius: 14, cursor: 'pointer', textAlign: 'left',
      display: 'flex', alignItems: 'center', gap: 12, fontFamily: FONT,
    }}>
      <div style={{
        width: 20, height: 20, borderRadius: 10,
        border: `2px solid ${selected ? T.teal : '#ccc'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {selected && <div style={{ width: 10, height: 10, borderRadius: 5, background: T.teal }} />}
      </div>
      <div>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: T.text }}>{label}</div>
        {desc && <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.4, marginTop: 1 }}>{desc}</div>}
      </div>
    </button>
  );
}

/* ═══════════════════════════════════════════════════════
   ConvergenceArc — SVG pricing visualization
   ═══════════════════════════════════════════════════════ */

function ConvergenceArc() {
  const CX = 170, CY = 172, R = 128, SW = 16, RB = 152;
  const scaleMax = 2000;
  const floor = 807, loLeg = 402, hiLeg = 1422, ask = 650;
  const TEAL_A = '#2C9C8C', TEAL_L = '#3BB8A6', TEAL_D = '#1F7A6D', NAVY = '#243A4E', CORAL = '#E8735A';

  const ap = (v: number, r: number) => {
    const p = Math.max(0, Math.min(1, v / scaleMax));
    const a = Math.PI * (1 - p);
    return { x: CX + r * Math.cos(a), y: CY - r * Math.sin(a) };
  };
  const radOff = (v: number, r: number, d: number) => {
    const p = Math.max(0, Math.min(1, v / scaleMax));
    const a = Math.PI * (1 - p);
    return { x: CX + (r + d) * Math.cos(a), y: CY - (r + d) * Math.sin(a) };
  };
  const arcPts = (from: number, to: number, r: number) => {
    const lo = Math.min(from, to), hi = Math.max(from, to), steps = 48;
    const pts: string[] = [];
    for (let i = 0; i <= steps; i++) {
      const v = lo + (hi - lo) * (i / steps);
      const pt = ap(v, r);
      pts.push(`${pt.x.toFixed(1)},${pt.y.toFixed(1)}`);
    }
    return 'M ' + pts[0] + ' ' + pts.slice(1).map(p => 'L ' + p).join(' ');
  };
  const anc = (v: number) => {
    const p = v / scaleMax;
    return p < 0.3 ? 'end' : p > 0.7 ? 'start' : 'middle';
  };
  const floorRot = (v: number) => {
    const p = Math.max(0, Math.min(1, v / scaleMax));
    return -(180 * (1 - p) - 90);
  };

  const floorPt = ap(floor, R), askPt = ap(ask, R);
  const aS = ap(0, R), aE = ap(scaleMax, R);
  const bLo = ap(loLeg, RB), bHi = ap(hiLeg, RB);
  const bLoM = ap(loLeg, R), bHiM = ap(hiLeg, R);
  const fR = floorRot(floor);
  const belowFloor = ask < floor;
  const loLabel = radOff(loLeg, RB, 12);
  const hiLabel = radOff(hiLeg, RB, 12);
  const ticks: { inner: { x: number; y: number }; outer: { x: number; y: number } }[] = [];
  for (let i = 1; i <= 5; i++) {
    const v = (scaleMax / 6) * i;
    ticks.push({ inner: ap(v, R - 10), outer: ap(v, R + 10) });
  }

  return (
    <svg viewBox="0 0 340 210" style={{ display: 'block', width: '100%', overflow: 'visible' }}>
      <defs>
        <linearGradient id="s1bandG" x1={bLo.x} y1={bLo.y} x2={bHi.x} y2={bHi.y} gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={TEAL_L} /><stop offset="50%" stopColor={TEAL_A} /><stop offset="100%" stopColor={TEAL_D} />
        </linearGradient>
        <linearGradient id="s1bgG" x1={aS.x} y1={aS.y} x2={aE.x} y2={aE.y} gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#ECEAE4" stopOpacity="1" /><stop offset="65%" stopColor="#ECEAE4" stopOpacity="1" />
          <stop offset="85%" stopColor="#ECEAE4" stopOpacity="0.4" /><stop offset="100%" stopColor="#ECEAE4" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="s1coralG" x1={aS.x} y1={aS.y} x2={floorPt.x} y2={floorPt.y} gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={CORAL} stopOpacity="0.55" /><stop offset="25%" stopColor={CORAL} stopOpacity="0.50" />
          <stop offset="100%" stopColor={CORAL} stopOpacity="0.85" />
        </linearGradient>
        <linearGradient id="s1tealG" x1={floorPt.x} y1={floorPt.y} x2={aE.x} y2={aE.y} gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={TEAL_A} stopOpacity="0.92" /><stop offset="12%" stopColor={TEAL_A} stopOpacity="0.80" />
          <stop offset="35%" stopColor={TEAL_A} stopOpacity="0.55" /><stop offset="58%" stopColor={TEAL_A} stopOpacity="0.30" />
          <stop offset="78%" stopColor={TEAL_A} stopOpacity="0.12" /><stop offset="100%" stopColor={TEAL_A} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Outer band */}
      <path d={arcPts(loLeg, hiLeg, RB)} fill="none" stroke="url(#s1bandG)" strokeWidth="7" opacity="0.28" strokeLinecap="round" />
      <path d={arcPts(loLeg, hiLeg, RB)} fill="none" stroke="url(#s1bandG)" strokeWidth="3" opacity="0.45" strokeLinecap="round" />
      <line x1={bLo.x} y1={bLo.y} x2={bLoM.x} y2={bLoM.y} stroke={TEAL_A} strokeWidth="0.8" opacity="0.2" />
      <line x1={bHi.x} y1={bHi.y} x2={bHiM.x} y2={bHiM.y} stroke={TEAL_A} strokeWidth="0.8" opacity="0.2" />
      <text x={loLabel.x} y={loLabel.y + 3} textAnchor={anc(loLeg)} fontFamily={MONO} fontSize="8.5" fontWeight="700" fill={NAVY} opacity="0.72">$402</text>
      <text x={hiLabel.x} y={hiLabel.y + 3} textAnchor={anc(hiLeg)} fontFamily={MONO} fontSize="8.5" fontWeight="700" fill={NAVY} opacity="0.72">$1,422</text>
      {/* Main arc */}
      <path d={arcPts(0, scaleMax, R)} fill="none" stroke="url(#s1bgG)" strokeWidth={SW} strokeLinecap="butt" />
      <path d={arcPts(0, floor, R)} fill="none" stroke="url(#s1coralG)" strokeWidth={SW} strokeLinecap="round" />
      <path d={arcPts(floor, scaleMax, R)} fill="none" stroke="url(#s1tealG)" strokeWidth={SW} />
      {/* Ticks */}
      {ticks.map((t, i) => (
        <line key={i} x1={t.inner.x} y1={t.inner.y} x2={t.outer.x} y2={t.outer.y} stroke="#dcd8d4" strokeWidth="0.6" />
      ))}
      {/* Ask price dot */}
      <circle cx={askPt.x} cy={askPt.y} r="12" fill="none" stroke={belowFloor ? CORAL : TEAL_A} strokeWidth="1" opacity="0.15" />
      <circle cx={askPt.x} cy={askPt.y} r="8" fill={belowFloor ? CORAL : TEAL_A} stroke="white" strokeWidth="3" />
      <circle cx={askPt.x} cy={askPt.y} r="2.5" fill="white" opacity="0.25" />
      {(() => { const lbl = radOff(ask, R, -24); return <text x={lbl.x} y={lbl.y + 3} textAnchor="middle" fontFamily={MONO} fontSize="9" fontWeight="700" fill={belowFloor ? CORAL : TEAL_A}>$650</text>; })()}
      {/* Floor needle */}
      <g transform={`translate(${floorPt.x},${floorPt.y}) rotate(${fR})`}>
        <rect x="-3.5" y="-14" width="7" height="28" rx="3.5" fill="#0B0C13" opacity="0.80" />
      </g>
      {(() => { const fl = radOff(floor, R, -28); return <text x={fl.x} y={fl.y + 3} textAnchor="middle" fontFamily={FONT} fontSize="7.5" fontWeight="800" fill="#777" letterSpacing="0.06em">FLOOR</text>; })()}
      <text x={CX - R - 8} y={CY + 20} textAnchor="start" style={{ fontFamily: MONO, fontSize: 8.5, fontWeight: 500, fill: '#c4c0ba' }}>$0</text>
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════
   Screen Components
   ═══════════════════════════════════════════════════════ */

/* Screen 0 — Welcome */
function Screen0({ onNext }: { onNext: () => void }) {
  const [show, setShow] = useState(false);
  useEffect(() => { setTimeout(() => setShow(true), 150); }, []);
  return (
    <div style={{ padding: '0 20px 24px', display: 'flex', flexDirection: 'column', minHeight: '100%' }}>

      <GlassCard style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Tag>Forensic Art Pricing</Tag>
        <div style={{
          opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(12px)',
          transition: 'all 0.6s cubic-bezier(0.34,1.56,0.64,1)',
        }}>
          <H1 style={{ fontSize: 28, lineHeight: 1.1 }}>
            You deserve to know{'\n'}your number.
          </H1>
        </div>
        <div style={{
          opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(12px)',
          transition: 'all 0.5s ease 0.15s',
        }}>
          <Body style={{ marginTop: 14, marginBottom: 20, flex: 1 }}>
            Most painters price by gut. TruePrice calculates the exact hourly rate below which you lose money, on every piece, every time.
          </Body>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{
          opacity: show ? 1 : 0, transition: 'opacity 0.4s ease 0.35s',
          background: 'rgba(61,171,165,0.05)', borderRadius: 14, padding: '14px 16px', marginBottom: 18,
          border: `1px solid ${T.border}`,
        }}>
          <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.5, fontFamily: FONT }}>
            <span style={{ fontWeight: 700, color: T.teal }}>90 seconds.</span>{' '}
            Three questions about your costs. One number. Your floor.
          </div>
        </div>
        <Btn onClick={onNext}>Begin</Btn>
      </GlassCard>
    </div>
  );
}

/* Screen 1 — Convergence Arc */
function Screen1({ onNext }: { onNext: () => void }) {
  const [show, setShow] = useState(false);
  useEffect(() => { setTimeout(() => setShow(true), 100); }, []);
  const NAVY = '#243A4E';
  return (
    <div style={{ padding: '0px 20px 10px', display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <Tag>Common Methods</Tag>
      <div style={{ opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(10px)', transition: 'all 0.5s ease' }}>
        <H1 style={{ marginBottom: 0, fontSize: 21 }}>Three methods. One range.</H1>
        <Body style={{ marginBottom: 0, fontSize: 12 }}>
          Traditional pricing produces wildly different numbers. TruePrice maps where they converge.
        </Body>
      </div>
      <div style={{
        position: 'relative', opacity: show ? 1 : 0,
        transform: show ? 'scale(1)' : 'scale(0.94)',
        transition: 'all 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.1s',
      }}>
        <div style={{ padding: '0 4px' }}><ConvergenceArc /></div>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none' }}>
          <span style={{ fontFamily: MONO, fontSize: 26, fontWeight: 800, color: NAVY, letterSpacing: '-0.02em' }}>$807</span>
          <span style={{ fontFamily: FONT, fontSize: 9, fontWeight: 700, color: '#a8a4a0', letterSpacing: '0.1em', marginTop: 4 }}>BREAKEVEN FLOOR</span>
        </div>
      </div>
      {/* Method comparison */}
      <div style={{
        opacity: show ? 1 : 0, transition: 'all 0.4s ease 0.25s',
        marginTop: 8, padding: '8px 14px',
        background: T.surface, backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
        borderRadius: 14, border: `1px solid ${T.border}`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
          {[
            { name: 'Cost-Based', val: '$402', detail: '$149 × 2.7' },
            { name: 'Hourly Rate', val: '$750', detail: '(24h × $25) + $150' },
            { name: 'Square Inch', val: '$1,422', detail: '1,200 in² × $1.19' },
          ].map((m, i) => (
            <div key={i} style={{ textAlign: 'center', flex: 1, padding: '2px 0' }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: T.teal, fontFamily: FONT, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 2 }}>{m.name}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: NAVY, fontFamily: MONO }}>{m.val}</div>
              <div style={{ fontSize: 8, fontWeight: 500, color: '#999', fontFamily: MONO, marginTop: 2, lineHeight: 1.3 }}>{m.detail}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{
        opacity: show ? 1 : 0, transition: 'all 0.4s ease 0.4s',
        marginTop: 6, padding: '7px 12px',
        background: 'rgba(61,171,165,0.04)', borderRadius: 10, border: `1px solid ${T.border}`,
      }}>
        <div style={{ fontSize: 11, color: T.textSub, lineHeight: 1.35, fontFamily: FONT }}>
          <span style={{ fontWeight: 700, color: T.teal }}>Your floor</span> is the price below which you lose money. It sits inside this range, calculated from your actual costs.
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 4 }} />
      <div style={{ marginTop: 4 }}><Btn onClick={onNext}>Continue</Btn></div>
    </div>
  );
}

/* Screen 2 — Forensic Roast teaser with bottomsheet animation */
function ScreenRoast({ onNext }: { onNext: () => void }) {
  /* Text fades in once (phase 1). Card/sheet loop independently.
     cardState: 'hidden' | 'showing' | 'tapping' | 'gone'
     sheetState: 'hidden' | 'up' | 'scrolling' | 'down' */
  const [textIn, setTextIn] = useState(false);
  const [cardState, setCardState] = useState<'hidden' | 'showing' | 'tapping' | 'gone'>('hidden');
  const [sheetState, setSheetState] = useState<'hidden' | 'up' | 'down'>('hidden');
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    if (!document.getElementById('roast-scroll-kf')) {
      const s = document.createElement('style');
      s.id = 'roast-scroll-kf';
      s.textContent = [
        '@keyframes roastDrift { 0%,12% { transform: translateY(0); } 44%,56% { transform: translateY(-90px); } 88%,100% { transform: translateY(0); } }',
        '@keyframes pulse { 0%,100% { opacity: 0.2; transform: scale(0.8); } 50% { opacity: 0.7; transform: scale(1.2); } }',
        '@keyframes sheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }',
        '@keyframes sheetDown { from { transform: translateY(0); opacity: 1; } to { transform: translateY(100%); opacity: 0; } }',
        '@keyframes tapPulse { 0% { transform: scale(1); box-shadow: 0 2px 8px rgba(0,0,0,0.04); } 50% { transform: scale(0.97); box-shadow: 0 1px 4px rgba(0,0,0,0.08); } 100% { transform: scale(1); box-shadow: 0 2px 8px rgba(0,0,0,0.04); } }',
      ].join('\n');
      document.head.appendChild(s);
    }
    // Text fades in once
    let alive = true;
    const ids: ReturnType<typeof setTimeout>[] = [];
    const t = (fn: () => void, ms: number) => { const id = setTimeout(() => { if (alive) fn(); }, ms); ids.push(id); };

    t(() => setTextIn(true), 300);

    const runCycle = () => {
      if (!alive) return;
      setCardState('showing');
      setSheetState('hidden');
      t(() => setCardState('tapping'), 1600);
      t(() => { setCardState('gone'); setSheetState('up'); setCycle(c => c + 1); }, 2100);
      t(() => setSheetState('down'), 18100);
      t(() => runCycle(), 18700);
    };
    t(() => runCycle(), 800);
    return () => { alive = false; ids.forEach(clearTimeout); };
  }, []);

  const star = (sz: number) => <span style={{ fontSize: sz, color: T.teal, lineHeight: 1 }}>✦</span>;
  const tp = (txt: string) => <span style={{ fontWeight: 700, fontSize: 'inherit', textDecoration: 'underline', textDecorationColor: 'rgba(61,171,165,0.4)', textUnderlineOffset: '2px', color: T.text, fontFamily: MONO }}>{txt}</span>;
  const cp = (txt: string) => <span style={{ fontWeight: 700, fontSize: 'inherit', textDecoration: 'underline', textDecorationColor: 'rgba(224,122,61,0.4)', textUnderlineOffset: '2px', color: T.text, fontFamily: MONO }}>{txt}</span>;
  const secDiv = (label: string, first: boolean) => (
    <div style={{ fontSize: 7, fontWeight: 700, letterSpacing: '1.2px', color: T.textMuted, textTransform: 'uppercase', marginTop: first ? 0 : 12, marginBottom: 6, paddingTop: first ? 0 : 10, borderTop: first ? 'none' : `1px solid ${T.border}`, fontFamily: FONT }}>{label}</div>
  );
  const dotBrk = (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 3, margin: '8px 0' }}>
      {[0, 1, 2].map(i => <span key={i} style={{ width: 2.5, height: 2.5, borderRadius: '50%', background: T.border }} />)}
    </div>
  );

  const f1: React.CSSProperties = { opacity: textIn ? 1 : 0, transform: textIn ? 'translateY(0)' : 'translateY(10px)', transition: 'all 0.5s ease' };
  const cardShow: React.CSSProperties = {
    opacity: cardState === 'showing' || cardState === 'tapping' ? 1 : 0,
    transform: cardState === 'showing' || cardState === 'tapping' ? 'translateY(0)' : 'translateY(16px)',
    transition: 'all 0.5s cubic-bezier(0.22,1,0.36,1)',
  };

  /* Insights teaser card */
  const insightsCard = (
    <div style={{
      ...cardShow,
      position: 'relative', width: '72%', maxWidth: 240, margin: '0 auto',
      background: '#FFFFFF', borderRadius: '16px 16px 12px 12px',
      boxShadow: '0 -2px 12px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)',
      overflow: 'hidden', pointerEvents: 'none', userSelect: 'none',
      animation: cardState === 'tapping' ? 'tapPulse 0.4s ease' : 'none',
    }}>
      <div style={{ padding: '12px 14px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
        {star(11)}
        <span style={{ fontSize: 8, fontWeight: 600, letterSpacing: '1px', color: T.teal, textTransform: 'uppercase', fontFamily: FONT }}>Forensic Roast</span>
      </div>
      <div style={{ padding: '8px 14px 0' }}>
        <p style={{ fontSize: 10.5, fontWeight: 500, color: T.text, lineHeight: 1.45, margin: 0, fontFamily: FONT }}>
          {'\u201C'}TruePrice by The Water{'\u201D'} sold for $157 below floor, costing you portfolio credibility.
        </p>
      </div>
      <div style={{ padding: '10px 14px', borderTop: `1px solid ${T.borderLight}`, marginTop: 10, textAlign: 'center' }}>
        <span style={{ fontSize: 8.5, color: T.textMuted, fontFamily: FONT }}>See full roast →</span>
      </div>
    </div>
  );

  /* Bottomsheet */
  const bottomSheet = (
    <div style={{
      width: '72%', maxWidth: 240, alignSelf: 'center',
      display: 'flex', flexDirection: 'column', maxHeight: '100%',
      animation: sheetState === 'up' ? 'sheetUp 0.45s cubic-bezier(0.32,0.72,0,1) forwards' : sheetState === 'down' ? 'sheetDown 0.5s ease forwards' : 'none',
      pointerEvents: 'none', userSelect: 'none',
      borderRadius: '16px 16px 10px 10px',
      background: '#FFFFFF',
      boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 -2px 12px rgba(0,0,0,0.04)',
      overflow: 'hidden', minHeight: 0,
    }}>
      {/* Sticky header: handle + title */}
      <div style={{ flexShrink: 0, background: '#FFFFFF', zIndex: 2, borderBottom: `1px solid ${T.borderLight}` }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px' }}>
          <div style={{ width: 32, height: 3.5, borderRadius: 2, background: T.border }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 14px 10px' }}>
          <div style={{ width: 24, height: 24, borderRadius: 7, background: 'rgba(61,171,165,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{star(12)}</div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.text, fontFamily: FONT, lineHeight: 1.2 }}>Forensic Roast</div>
            <div style={{ fontSize: 8, color: T.textMuted, fontFamily: FONT, marginTop: 1 }}>Updated just now</div>
          </div>
        </div>
      </div>
      {/* Scrolling content area */}
      <div style={{ overflow: 'hidden', position: 'relative' }}>
        <div key={cycle} style={{ animation: 'roastDrift 16s linear 0s 1 forwards', padding: '8px 14px 14px' }}>
          {secDiv('The Finding', true)}
          <p style={{ fontSize: 10.5, fontWeight: 600, color: T.text, lineHeight: 1.45, fontFamily: FONT, marginBottom: 8, marginTop: 0 }}>
            You sold {'\u201C'}TruePrice by The Water{'\u201D'} for {cp('$650')} against a floor of {tp('$807')}.
          </p>
          <p style={{ fontSize: 9.5, color: T.textSub, lineHeight: 1.6, fontFamily: FONT, marginBottom: 0, marginTop: 0 }}>
            This {cp('$157 loss')} signals your pricing is not holding. One of your only two lifetime sales dropped below floor, which tells buyers your work does not retain value.
          </p>
          {dotBrk}
          <p style={{ fontSize: 9.5, color: T.textSub, lineHeight: 1.6, fontFamily: FONT, marginBottom: 0, marginTop: 0 }}>
            {'\u201C'}Cultural Masterpiece{'\u201D'} sold for {tp('$650')} and stayed above its {tp('$611')} floor. But {'\u201C'}TruePrice by The Water{'\u201D'} at {cp('$650')} fell {cp('$157')} short of {tp('$807')}. You have 1 active piece and no safety margin on either sold work.
          </p>
          {secDiv('The Move', false)}
          <div style={{ background: 'rgba(61,171,165,0.08)', borderRadius: 8, padding: '8px 10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
              {star(8)}
              <span style={{ fontSize: 8.5, fontWeight: 700, color: T.teal, fontFamily: FONT, letterSpacing: '0.02em' }}>Recommended action</span>
            </div>
            <p style={{ fontSize: 9.5, color: T.textSub, lineHeight: 1.55, fontFamily: FONT, margin: 0 }}>
              Reprice {'\u201C'}TruePrice by The Water{'\u201D'} from {cp('$650')} to {tp('$807')} on any platform where it appears. This recovers {tp('$157')} per future sale and lifts your portfolio score from {tp('86')} to {tp('88')}.
            </p>
          </div>
          {/* Portfolio health footer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, padding: '8px 0', borderTop: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: T.text, fontFamily: FONT }}>86</span>
              <span style={{ fontSize: 8, color: T.textMuted, fontFamily: FONT }}>Portfolio health: 86/100</span>
            </div>
            <span style={{ fontSize: 8, color: T.textMuted, fontFamily: FONT }}>0 of 1 below floor</span>
          </div>
        </div>
        {/* Bottom fade */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 24, background: 'linear-gradient(transparent, #FFFFFF)', borderRadius: '0 0 10px 10px', pointerEvents: 'none' }} />
      </div>
    </div>
  );

  return (
    <div style={{ padding: '0 20px 10px', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Tag>Forensic Insights</Tag>
      <div style={f1}>
        <H1 style={{ marginBottom: 4, fontSize: 21 }}>Your pricing, analyzed by AI.</H1>
        <Body style={{ marginBottom: 12, fontSize: 12.5 }}>
          A forensic AI analysis that reads your costs, your sales history, and flags exactly where you{'\u2019'}re losing money.
        </Body>
      </div>
      {/* Illustration area — card or bottomsheet */}
      <div style={{
        position: 'relative', flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'flex-start',
        paddingTop: cardState !== 'gone' ? 20 : 0,
        minHeight: 0, overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%,-50%)',
          width: '80%', height: '70%', borderRadius: '50%',
          background: 'radial-gradient(ellipse, rgba(61,171,165,0.10) 0%, transparent 70%)', pointerEvents: 'none',
        }} />
        {cardState !== 'gone' && insightsCard}
        {cardState === 'gone' && bottomSheet}
      </div>
      <div style={{ flexShrink: 0, paddingTop: 16, paddingBottom: 4 }}>
        <Btn onClick={onNext}>Continue</Btn>
      </div>
    </div>
  );
}

/* Screen 3 — Bridge */
function ScreenBridge({ onNext }: { onNext: () => void }) {
  const [show, setShow] = useState(false);
  useEffect(() => { setTimeout(() => setShow(true), 100); }, []);

  const items = [
    {
      num: '01', label: 'Monthly burn', desc: 'What it costs you to exist each month',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" stroke={T.teal} strokeWidth="1.6" /><path d="M12 6v6l4 2" stroke={T.teal} strokeWidth="1.6" strokeLinecap="round" /></svg>,
    },
    {
      num: '02', label: 'Studio hours', desc: 'Realistic time spent painting, not admin',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 19a7 7 0 100-14 7 7 0 000 14z" stroke={T.teal} strokeWidth="1.6" /><path d="M12 9v4l2.5 1.5" stroke={T.teal} strokeWidth="1.6" strokeLinecap="round" /><path d="M16.5 7.5L18 6M7.5 7.5L6 6" stroke={T.teal} strokeWidth="1.6" strokeLinecap="round" /></svg>,
    },
    {
      num: '03', label: 'Location', desc: 'Market context for regional pricing',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke={T.teal} strokeWidth="1.6" /><circle cx={12} cy={9} r={2.5} stroke={T.teal} strokeWidth="1.6" /></svg>,
    },
  ];

  return (
    <div style={{ padding: '8px 20px 24px', display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <Tag>Setup</Tag>
      <div style={{ opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(10px)', transition: 'all 0.5s ease' }}>
        <H1 style={{ marginBottom: 6 }}>Three inputs.{'\n'}That's it.</H1>
        <Body style={{ marginBottom: 24 }}>TruePrice calculates your floor from real numbers. Takes about 90 seconds.</Body>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((item, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
            background: T.surface, backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
            borderRadius: 16, border: '1px solid rgba(232,229,222,0.6)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.02)',
            opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(10px)',
            transition: `all 0.45s cubic-bezier(0.22,1,0.36,1) ${0.2 + i * 0.1}s`,
          }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{ width: 44, height: 44, borderRadius: 13, background: 'rgba(61,171,165,0.07)', border: '1px solid rgba(61,171,165,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {item.icon}
              </div>
              <div style={{ position: 'absolute', top: -4, right: -4, width: 18, height: 18, borderRadius: 6, background: T.teal, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', fontFamily: FONT }}>{item.num}</span>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14.5, fontWeight: 600, color: T.text, fontFamily: FONT, lineHeight: 1.2, marginBottom: 2 }}>{item.label}</div>
              <div style={{ fontSize: 12, color: T.textSub, fontFamily: FONT, lineHeight: 1.4 }}>{item.desc}</div>
            </div>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, opacity: 0.25 }}>
              <path d="M6 3l5 5-5 5" stroke={T.text} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        ))}
      </div>
      <div style={{ flex: 1 }} />
      <Btn onClick={onNext}>Continue</Btn>
    </div>
  );
}

/* Screen 4 — Monthly Burn (Plaid + manual) */
function ScreenBurn({ burn, onBurnChange, onNext, onBankConnected }: {
  burn: number; onBurnChange: (v: number) => void; onNext: () => void;
  onBankConnected: (institutionName: string) => void;
}) {
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState<'plaid' | 'connecting' | 'connected' | 'manual'>('plaid');
  const [manualTouched, setManualTouched] = useState(false);
  const { connect, open, ready, loading, connected, error, institutionName } = usePlaidConnection();

  useEffect(() => { setTimeout(() => setShow(true), 100); }, []);

  useEffect(() => {
    if (connected && institutionName) {
      setMode('connected');
      onBankConnected(institutionName);
    }
  }, [connected, institutionName, onBankConnected]);

  const handleConnectBank = async () => {
    setMode('connecting');
    await connect();
  };

  // Manual entry allows immediate Continue (default value is acceptable).
  // Plaid is gated off on free tier during onboarding — premium flag, not tappable.
  const canProceed = mode === 'connected' || mode === 'manual';

  // Connected confirmation
  if (mode === 'connected') {
    return (
      <div style={{ padding: '8px 20px 24px', display: 'flex', flexDirection: 'column', minHeight: '100%' }}>

        <Tag>Survival Economics</Tag>
        <div style={{ opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(10px)', transition: 'all 0.5s ease' }}>
          <H1 style={{ marginBottom: 6 }}>What does it cost{'\n'}you to exist?</H1>
          <Body style={{ marginBottom: 20 }}>Rent. Food. Insurance. Supplies. Subscriptions. Total monthly operating cost.</Body>
        </div>
        <GlassCard padding="20px" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(61,171,165,0.08)', border: '1.5px solid rgba(61,171,165,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17l-5-5" stroke={T.teal} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: T.text, fontFamily: FONT, marginBottom: 2 }}>Bank connected</div>
              <div style={{ fontSize: 12, color: T.textSub, fontFamily: FONT, lineHeight: 1.4 }}>Read-only access. Spending data will sync to your dashboard.</div>
            </div>
          </div>
        </GlassCard>
        <button onClick={() => { setMode('manual'); }} style={{
          display: 'block', width: '100%', fontFamily: FONT, fontSize: 12, fontWeight: 500,
          color: T.textMuted, textAlign: 'center', background: 'transparent', border: 'none',
          cursor: 'pointer', padding: '8px', textDecoration: 'underline', textUnderlineOffset: '3px',
        }}>Enter manually instead</button>
        <div style={{ flex: 1 }} />
        <div style={{ marginTop: 20 }}><Btn onClick={onNext}>Continue</Btn></div>
      </div>
    );
  }

  // Plaid initial / connecting
  if (mode === 'plaid' || mode === 'connecting') {
    return (
      <div style={{ padding: '8px 20px 24px', display: 'flex', flexDirection: 'column', minHeight: '100%' }}>

        <Tag>Survival Economics</Tag>
        <div style={{ opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(10px)', transition: 'all 0.5s ease' }}>
          <H1 style={{ marginBottom: 6 }}>What does it cost{'\n'}you to exist?</H1>
          <Body style={{ marginBottom: 20 }}>Rent. Food. Insurance. Supplies. Subscriptions. Total monthly operating cost.</Body>
        </div>
        {error && (
          <div style={{ padding: '10px 14px', borderRadius: 10, marginBottom: 16, background: 'rgba(224,122,61,0.08)', color: '#C05A20', fontSize: 13, fontFamily: FONT, lineHeight: 1.4 }}>
            {error}
          </div>
        )}
        <div style={{ opacity: show ? 1 : 0, transition: 'all 0.4s ease 0.15s' }}>
          <GlassCard padding="20px" style={{ textAlign: 'center', marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: T.tealLight, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M19 5H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V7a2 2 0 00-2-2z" stroke={T.teal} strokeWidth="1.8" />
                <path d="M3 10h18" stroke={T.teal} strokeWidth="1.8" />
              </svg>
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text, fontFamily: FONT, marginBottom: 4 }}>Connect your bank</div>
            <div style={{ fontSize: 12.5, color: T.textSub, lineHeight: 1.45, fontFamily: FONT, marginBottom: 16 }}>Connect your bank to track real spending. Read-only access.</div>
            <div style={{ opacity: 0.5, pointerEvents: 'none', position: 'relative' }}>
              <div style={{
                width: '100%', padding: '13px 20px', background: 'transparent', color: T.teal,
                border: `1.5px solid ${T.teal}`, borderRadius: 12, fontSize: 14, fontWeight: 600,
                fontFamily: FONT, textAlign: 'center',
              }}>
                Connect with Plaid
              </div>
              <span style={{
                position: 'absolute', top: -8, right: -6,
                padding: '3px 8px', borderRadius: 6,
                fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: T.teal, background: T.surfSolid,
                border: `1px solid ${T.tealLight}`,
                fontFamily: FONT,
              }}>PREMIUM</span>
            </div>
          </GlassCard>
          <button onClick={() => setMode('manual')} style={{
            display: 'block', width: '100%', fontFamily: FONT, fontSize: 13, fontWeight: 500,
            color: T.textMuted, textAlign: 'center', background: 'transparent', border: 'none',
            cursor: 'pointer', padding: '8px',
          }}>Enter manually instead</button>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ marginTop: 20 }}><Btn onClick={onNext} disabled={!canProceed}>Continue</Btn></div>
      </div>
    );
  }

  // Manual mode
  return (
    <div style={{ padding: '8px 20px 24px', display: 'flex', flexDirection: 'column', minHeight: '100%' }}>

      <Tag>Survival Economics</Tag>
      <div style={{ opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(10px)', transition: 'all 0.5s ease' }}>
        <H1 style={{ marginBottom: 6 }}>What does it cost{'\n'}you to exist?</H1>
        <Body style={{ marginBottom: 20 }}>Rent. Food. Insurance. Supplies. Subscriptions. Total monthly operating cost.</Body>
      </div>
      <div style={{ opacity: show ? 1 : 0, transition: 'all 0.4s ease 0.15s' }}>
        <Stepper value={burn} onChange={(v) => { onBurnChange(v); setManualTouched(true); }} prefix="$" step={100} min={500} max={25000} />
        <div style={{ textAlign: 'center', fontSize: 12, color: T.textMuted, fontFamily: FONT, padding: '14px 0' }}>
          Monthly total before you pick up a brush.
        </div>
        <button onClick={() => setMode('plaid')} style={{
          display: 'block', width: '100%', fontFamily: FONT, fontSize: 13, fontWeight: 500,
          color: T.textMuted, textAlign: 'center', background: 'transparent', border: 'none',
          cursor: 'pointer', padding: '8px',
        }}>Connect bank instead</button>
      </div>
      <div style={{ flex: 1 }} />
      <div style={{ marginTop: 20 }}><Btn onClick={onNext} disabled={!canProceed}>Continue</Btn></div>
    </div>
  );
}

/* Screen 5 — Studio Hours */
function ScreenHours({ capacity, onCapacityChange, onNext }: {
  capacity: number; onCapacityChange: (v: number) => void; onNext: () => void;
}) {
  const [show, setShow] = useState(false);
  useEffect(() => { setTimeout(() => setShow(true), 100); }, []);
  const weeklyEquiv = Math.round(capacity / 4.33);
  const workLabel = weeklyEquiv >= 35 ? 'Full time' : 'Part time';
  const workColor = weeklyEquiv >= 35 ? T.teal : T.textMuted;
  return (
    <div style={{ padding: '8px 20px 24px', display: 'flex', flexDirection: 'column', minHeight: '100%' }}>

      <Tag>Survival Economics</Tag>
      <div style={{ opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(10px)', transition: 'all 0.5s ease' }}>
        <H1 style={{ marginBottom: 6 }}>How many hours a month{'\n'}do you actually paint?</H1>
        <Body style={{ marginBottom: 24 }}>Studio time only. Not admin, marketing, or errands.</Body>
      </div>
      <div style={{ opacity: show ? 1 : 0, transition: 'all 0.4s ease 0.15s' }}>
        <Stepper value={capacity} onChange={onCapacityChange} prefix="" suffix=" hrs/mo" step={5} min={20} max={300} />
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        padding: '14px 0', marginBottom: 'auto',
        opacity: show ? 1 : 0, transition: 'opacity 0.4s ease 0.3s',
      }}>
        <span style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT }}>≈ {weeklyEquiv} hrs/week</span>
        <span style={{
          fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
          color: workColor, fontFamily: FONT, padding: '3px 8px',
          background: weeklyEquiv >= 35 ? 'rgba(61,171,165,0.08)' : 'rgba(138,155,146,0.08)', borderRadius: 6,
        }}>{workLabel}</span>
      </div>
      <div style={{ marginTop: 20 }}><Btn onClick={onNext}>Continue</Btn></div>
    </div>
  );
}

/* Screen 6 — Career Stage */
function ScreenCareer({ careerStage, onCareerChange, onNext }: {
  careerStage: CareerStageKey | ''; onCareerChange: (v: CareerStageKey) => void; onNext: () => void;
}) {
  const [show, setShow] = useState(false);
  useEffect(() => { setTimeout(() => setShow(true), 100); }, []);
  return (
    <div style={{ padding: '8px 20px 24px', display: 'flex', flexDirection: 'column', minHeight: '100%' }}>

      <Tag>Market Position</Tag>
      <div style={{ opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(10px)', transition: 'all 0.5s ease' }}>
        <H1 style={{ marginBottom: 6 }}>Where are you{'\n'}in your career?</H1>
        <Body style={{ marginBottom: 18 }}>Shapes how TruePrice frames your pricing range.</Body>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
        {CAREER_ITEMS.map((s, i) => (
          <div key={s.id} style={{
            opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(8px)',
            transition: `all 0.4s ease ${0.1 + i * 0.06}s`,
          }}>
            <Radio label={s.label} desc={s.desc} selected={careerStage === s.id} onClick={() => onCareerChange(s.id)} />
          </div>
        ))}
      </div>
      <div style={{ marginTop: 16 }}><Btn onClick={onNext} disabled={!careerStage}>Continue</Btn></div>
    </div>
  );
}

/* Screen 7 — Location (ZIP lookup) */
function ScreenLocation({ zip, city, stateName, onZipChange, onNext }: {
  zip: string; city: string; stateName: string;
  onZipChange: (v: string) => void; onNext: () => void;
}) {
  const [show, setShow] = useState(false);
  const [focused, setFocused] = useState(false);
  useEffect(() => { setTimeout(() => setShow(true), 100); }, []);
  const isValid = zip.length === 5 && /^\d{5}$/.test(zip);
  const hasLookup = !!(city && stateName);

  return (
    <div style={{ padding: '8px 20px 24px', display: 'flex', flexDirection: 'column', minHeight: '100%' }}>

      <Tag>Your Identity</Tag>
      <div style={{ opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(10px)', transition: 'all 0.5s ease' }}>
        <H1 style={{ marginBottom: 6 }}>Where are you based?</H1>
        <Body style={{ marginBottom: 24 }}>Used for market context.</Body>
      </div>
      <div style={{ opacity: show ? 1 : 0, transition: 'all 0.4s ease 0.15s' }}>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.textSub, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: FONT }}>ZIP Code</label>
        <input
          type="text" inputMode="numeric" value={zip}
          onChange={(e) => onZipChange(e.target.value.replace(/\D/g, '').slice(0, 5))}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          placeholder="—" maxLength={5}
          style={{
            width: 140, height: 52, padding: '0 16px', background: T.surfSolid,
            border: `1.5px solid ${focused ? T.teal : T.border}`, borderRadius: 12,
            fontSize: 20, fontWeight: 600, color: T.text, outline: 'none', boxSizing: 'border-box',
            fontFamily: FONT, letterSpacing: '0.15em', textAlign: 'center', transition: 'border-color 0.2s ease',
          }}
        />
        {isValid && (
          <div style={{
            marginTop: 16, padding: '14px 16px', width: '100%',
            background: hasLookup ? 'rgba(61,171,165,0.06)' : T.surfSolid,
            borderRadius: 14, border: `1.5px solid ${hasLookup ? 'rgba(61,171,165,0.2)' : T.border}`,
            display: 'flex', alignItems: 'center', gap: 12, transition: 'all 0.3s ease',
          }}>
            {hasLookup ? (
              <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(61,171,165,0.1)', border: '1px solid rgba(61,171,165,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M20 6L9 17l-5-5" stroke={T.teal} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke={T.textMuted} strokeWidth="1.8" />
                <circle cx="12" cy="10" r="3" stroke={T.textMuted} strokeWidth="1.8" />
              </svg>
            )}
            <div>
              <div style={{ fontFamily: FONT, fontSize: hasLookup ? 16 : 14, fontWeight: 600, color: hasLookup ? T.text : T.textMuted }}>
                {hasLookup ? `${city}, ${stateName}` : 'Looking up...'}
              </div>
              {hasLookup && (
                <div style={{ fontFamily: FONT, fontSize: 11, color: T.textMuted, marginTop: 2 }}>ZIP {zip}</div>
              )}
            </div>
          </div>
        )}
      </div>
      <div style={{ flex: 1 }} />
      <div style={{ marginTop: 24 }}><Btn onClick={onNext} disabled={!isValid}>Continue</Btn></div>
    </div>
  );
}

/* Screen 8 — Final (Forensic Ledger Card) */
function ScreenFinal({ burn, burnSource, institutionName, capacity, careerStage, city, stateName, zip, onComplete, onEdit }: {
  burn: number; burnSource?: string; institutionName?: string;
  capacity: number; careerStage: string;
  city: string; stateName: string; zip: string;
  onComplete: () => void; onEdit: () => void;
}) {
  const [phase, setPhase] = useState(0);
  const isPlaid = burnSource === 'plaid';
  const rate = !isPlaid && burn && capacity ? (burn / capacity).toFixed(2) : null;
  const rateParts = rate ? rate.split('.') : null;
  const stageLabel = careerStage ? careerStage.charAt(0).toUpperCase() + careerStage.slice(1) : '';
  const locationStr = city && stateName ? `${city}, ${stateName}` : zip ? `ZIP ${zip}` : '';
  const hoursPerWeek = capacity ? Math.round(capacity / 4.33) : 0;

  useEffect(() => {
    const t = [
      setTimeout(() => setPhase(1), 400),
      setTimeout(() => setPhase(2), 1200),
      setTimeout(() => setPhase(3), 2000),
    ];
    return () => t.forEach(clearTimeout);
  }, []);

  // GlassCard style
  const glass: React.CSSProperties = {
    background: T.surface, backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
    borderRadius: T.radius, border: `1px solid ${T.borderLight}`, boxShadow: T.shadow,
  };

  return (
    <div style={{ padding: '56px 24px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '100%', textAlign: 'center' }}>
      {/* H1 */}
      <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: -0.5, color: T.text, fontFamily: FONT, margin: 0, lineHeight: 1.2,
        opacity: phase >= 1 ? 1 : 0, transition: 'opacity 0.5s ease',
      }}>
        Setup complete.
      </h1>

      {/* Body */}
      <p style={{ fontSize: 15, color: T.textSub, fontFamily: FONT, margin: '8px 0 24px', lineHeight: 1.5,
        opacity: phase >= 1 ? 1 : 0, transition: 'opacity 0.5s ease 0.1s',
      }}>
        {isPlaid
          ? 'Your bank is connected. Real numbers replace defaults as data syncs.'
          : 'Your pricing floor. Below this, every hour costs you money.'}
      </p>

      {/* Forensic Ledger Card */}
      <div style={{
        ...glass, padding: 24, width: '100%', textAlign: 'center',
        opacity: phase >= 1 ? 1 : 0, transform: phase >= 1 ? 'translateY(0)' : 'translateY(8px)',
        transition: 'all 0.6s cubic-bezier(0.32, 0.72, 0, 1)',
      }}>
        {/* Tag */}
        <div style={{
          display: 'inline-block', fontSize: 11, fontWeight: 600, letterSpacing: 0.5,
          textTransform: 'uppercase', color: T.teal, background: T.tealLight,
          borderRadius: 8, padding: '4px 10px', marginBottom: 12,
        }}>
          {isPlaid ? 'BANK CONNECTED' : 'SURVIVAL RATE'}
        </div>

        {/* Hero content */}
        {isPlaid ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span style={{ fontSize: 16, fontWeight: 500, color: T.text, fontFamily: FONT }}>
                {institutionName || 'Bank account'}
              </span>
            </div>
            <p style={{ fontSize: 14, color: T.textMuted, fontFamily: FONT, margin: 0, lineHeight: 1.5 }}>
              Your survival rate will appear on your dashboard once data syncs.
            </p>
          </>
        ) : (
          <>
            <div style={{ display: 'inline-flex', alignItems: 'baseline' }}>
              <span style={{ fontSize: 36, fontWeight: 700, color: T.text, fontFamily: MONO, letterSpacing: -1 }}>
                ${rateParts?.[0] ?? '—'}
              </span>
              {rateParts?.[1] && (
                <span style={{ fontSize: 20, fontWeight: 700, color: T.text, fontFamily: MONO, opacity: 0.5 }}>.{rateParts[1]}</span>
              )}
              <span style={{ fontSize: 16, color: T.textMuted, fontFamily: FONT, marginLeft: 4 }}>/hr</span>
            </div>
            <p style={{ fontSize: 14, color: T.textMuted, fontFamily: FONT, margin: '8px 0 0', lineHeight: 1.5 }}>
              Below this, every hour you paint costs you money.
            </p>
          </>
        )}
      </div>

      {/* Summary Grid */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, width: '100%', marginTop: 16,
        opacity: phase >= 2 ? 1 : 0, transform: phase >= 2 ? 'translateY(0)' : 'translateY(8px)',
        transition: 'all 0.5s ease',
      }}>
        {[
          { icon: 'briefcase', label: 'CAREER', value: stageLabel },
          { icon: 'dollar', label: 'MONTHLY BURN', value: isPlaid ? null : `$${burn.toLocaleString()}`, tag: isPlaid ? 'TRACKED' : null },
          { icon: 'clock', label: 'STUDIO HOURS', value: `${hoursPerWeek} hrs/wk` },
          { icon: 'map', label: 'LOCATION', value: locationStr },
        ].map((cell, i) => (
          <div key={i} style={{
            ...glass, borderRadius: 12, padding: '12px 14px', textAlign: 'center',
          }}>
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 6 }}>
              {cell.icon === 'briefcase' && <><rect x={2} y={7} width={20} height={14} rx={2} /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></>}
              {cell.icon === 'dollar' && <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />}
              {cell.icon === 'clock' && <><circle cx={12} cy={12} r={10} /><path d="M12 6v6l4 2" /></>}
              {cell.icon === 'map' && <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx={12} cy={10} r={3} /></>}
            </svg>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', color: T.textMuted, fontFamily: FONT, marginBottom: 3 }}>
              {cell.label}
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text, fontFamily: FONT }}>
              {cell.tag ? (
                <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: T.teal, background: T.tealLight, borderRadius: 8, padding: '4px 10px' }}>
                  {cell.tag}
                </span>
              ) : cell.value}
            </div>
          </div>
        ))}
      </div>

      {/* Edit setup link */}
      <button onClick={onEdit} style={{
        display: 'block', marginTop: 20, fontFamily: FONT, fontSize: 14, fontWeight: 500,
        color: T.teal, background: 'transparent', border: 'none', cursor: 'pointer',
        padding: '10px', minHeight: 44,
        opacity: phase >= 2 ? 1 : 0, transition: 'opacity 0.4s ease',
      }}>Edit setup</button>

      <div style={{ flex: 1, minHeight: 20 }} />

      {/* CTA */}
      <div style={{ width: '100%', opacity: phase >= 3 ? 1 : 0, transition: 'opacity 0.4s ease' }}>
        <Btn onClick={onComplete}>Go to dashboard</Btn>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════ */

const TOTAL = 9;

export default function UnifiedOnboarding() {
  const { profile: rawProfile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [p, setSp] = useState<StudioProfile>(() => readStudioProfile(rawProfile));
  const [screen, setScreen] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [visible, setVisible] = useState(true);
  const [direction, setDirection] = useState(1);
  const [zip, setZip] = useState('');
  const [city, setCity] = useState('');
  const [bankConnectedAt, setBankConnectedAt] = useState<string | null>(null);
  const [institutionName, setInstitutionName] = useState<string | null>(null);
  const [stateName, setStateName] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setSp(readStudioProfile(rawProfile)); }, [rawProfile]);

  const up = useCallback(
    (key: keyof StudioProfile, value: any) => setSp((prev) => ({ ...prev, [key]: value })),
    []
  );

  const handleZipChange = (val: string) => {
    setZip(val);
    if (val.length === 5 && /^\d{5}$/.test(val)) {
      // Try sync cache first for instant feedback
      const cached = ZIP_CACHE[val];
      if (cached) {
        setCity(cached.city);
        setStateName(cached.state);
      } else {
        setCity('');
        setStateName('');
        // Async API lookup
        lookupZip(val).then((r) => {
          if (r) { setCity(r.city); setStateName(r.state); }
        });
      }
    } else { setCity(''); setStateName(''); }
  };

  const goTo = useCallback((n: number) => {
    if (animating) return;
    setDirection(n > screen ? 1 : -1);
    setAnimating(true);
    setVisible(false);
    setTimeout(() => {
      setScreen(n);
      setVisible(true);
      setAnimating(false);
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, 300);
  }, [animating, screen]);

  const next = () => goTo(Math.min(screen + 1, TOTAL - 1));
  const prev = () => goTo(Math.max(screen - 1, 0));

  const handleComplete = async () => {
    // Store location from ZIP lookup
    const loc = city && stateName ? `${city}, ${stateName}` : zip ? `ZIP ${zip}` : '';
    up('location', loc);
    const payload = { ...toProfileUpdate({ ...p, location: loc }), onboardingCompleted: true };
    await updateProfile(payload);
    const nextUrl = searchParams.get('next');
    navigate(nextUrl ? decodeURIComponent(nextUrl) : '/app/dashboard', { replace: true });
  };

  // Panorama band height
  const bandH = screen <= 1 ? 200 : screen >= 2 && screen <= 7 ? 110 : 0;
  const panX = screen * ZONE_W;

  return (
    <div style={{
      height: '100dvh', maxWidth: 430, margin: '0 auto',
      display: 'flex', flexDirection: 'column',
      background: T.bg, fontFamily: FONT,
      WebkitFontSmoothing: 'antialiased',
      position: 'relative', overflow: 'hidden',
    }}>
      <link href={FONT_LINK} rel="stylesheet" />

      {/* Status bar spacer */}
      <div style={{ height: 'env(safe-area-inset-top, 0px)', minHeight: 0, flexShrink: 0 }} />

      {/* Illustration band */}
      <div style={{
        position: 'relative', height: bandH, overflow: 'hidden',
        transition: 'height 0.5s cubic-bezier(0.4,0,0.2,1)', zIndex: 1, flexShrink: 0,
      }}>
        {bandH > 0 && (
          <div style={{
            transform: `translateX(-${panX}px)`,
            transition: 'transform 0.6s cubic-bezier(0.4,0,0.2,1)',
          }}>
            <Panorama />
          </div>
        )}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: Math.min(50, bandH * 0.35),
          background: `linear-gradient(180deg, transparent 0%, ${T.bg} 100%)`,
          zIndex: 2,
        }} />
      </div>

      {/* Back button */}
      {screen > 0 && screen < TOTAL - 1 && (
        <button onClick={prev} style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
          left: 16, zIndex: 20,
          width: 36, height: 36, borderRadius: 12,
          background: T.surface, backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
          border: `1px solid ${T.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 3L5 8L10 13" stroke={T.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {/* Progress dots */}
      <div style={{
        position: 'absolute',
        top: 'calc(env(safe-area-inset-top, 0px) + 14px)',
        left: 0, right: 0, zIndex: 15,
      }}>
        <Dots current={screen} total={TOTAL} />
      </div>

      {/* Content area */}
      <div ref={scrollRef} style={{
        flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : `translateX(${direction * 30}px)`,
        transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
        position: 'relative', zIndex: 2,
      }}>
        {screen === 0 && <Screen0 onNext={next} />}
        {screen === 1 && <Screen1 onNext={next} />}
        {screen === 2 && <ScreenRoast onNext={next} />}
        {screen === 3 && <ScreenBridge onNext={next} />}
        {screen === 4 && (
          <ScreenBurn
            burn={p.monthlyBurn}
            onBurnChange={(v) => up('monthlyBurn', v)}
            onNext={next}
            onBankConnected={(name) => { up('burnSource', 'plaid'); setBankConnectedAt(new Date().toISOString()); setInstitutionName(name); }}
          />
        )}
        {screen === 5 && (
          <ScreenHours
            capacity={p.monthlyCapacity}
            onCapacityChange={(v) => up('monthlyCapacity', v)}
            onNext={next}
          />
        )}
        {screen === 6 && (
          <ScreenCareer
            careerStage={p.careerStage}
            onCareerChange={(v) => up('careerStage', v)}
            onNext={next}
          />
        )}
        {screen === 7 && (
          <ScreenLocation
            zip={zip} city={city} stateName={stateName}
            onZipChange={handleZipChange}
            onNext={next}
          />
        )}
        {screen === 8 && (
          <ScreenFinal
            burn={p.monthlyBurn}
            burnSource={p.burnSource}
            institutionName={institutionName ?? undefined}
            capacity={p.monthlyCapacity}
            careerStage={p.careerStage}
            city={city} stateName={stateName} zip={zip}
            onComplete={handleComplete}
            onEdit={() => goTo(4)}
          />
        )}
      </div>

      {/* Home indicator */}
      <div style={{
        flexShrink: 0,
        paddingBottom: 'env(safe-area-inset-bottom, 8px)',
        display: 'flex', justifyContent: 'center', paddingTop: 4,
      }}>
        <div style={{ width: 134, height: 5, borderRadius: 3, background: '#1A1A1A', opacity: 0.2 }} />
      </div>
    </div>
  );
}